// CFO Buck → Agent tab — Skills Launcher + Categorize playbook.
//
// Surfaces a row of skill cards. Clicking a card runs the corresponding
// /api/agent/skill/{id}/plan endpoint and shows the Action Receipt
// modal. The user reviews + clicks "Looks good" to execute.
import { useEffect, useRef, useState } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Sparkles, Tag, RefreshCw, TrendingUp, Mail, Loader2, CheckCircle2,
  AlertTriangle, HelpCircle, Undo2, ArrowRight, Wand2, Settings, Send,
  Inbox, Link2, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  tag: Tag,
  refresh: RefreshCw,
  trending: TrendingUp,
  mail: Mail,
  sparkles: Sparkles,
  inbox: Inbox,
};

const COLOR_STYLES = {
  violet:  { bg: 'bg-violet-50 dark:bg-violet-950/40',   border: 'border-violet-200 dark:border-violet-900', text: 'text-violet-700 dark:text-violet-300',   accent: 'bg-violet-600' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/40', border: 'border-emerald-200 dark:border-emerald-900', text: 'text-emerald-700 dark:text-emerald-300', accent: 'bg-emerald-600' },
  blue:    { bg: 'bg-blue-50 dark:bg-blue-950/40',       border: 'border-blue-200 dark:border-blue-900',     text: 'text-blue-700 dark:text-blue-300',       accent: 'bg-blue-600' },
  amber:   { bg: 'bg-amber-50 dark:bg-amber-950/40',     border: 'border-amber-200 dark:border-amber-900',   text: 'text-amber-700 dark:text-amber-300',     accent: 'bg-amber-600' },
  rose:    { bg: 'bg-rose-50 dark:bg-rose-950/40',       border: 'border-rose-200 dark:border-rose-900',     text: 'text-rose-700 dark:text-rose-300',       accent: 'bg-rose-600' },
};

const fmtUSD = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
}).format(n || 0);

// Convert a chase_ar /plan receipt into the editable arEntries state.
const receiptToArEntries = (receipt) =>
  (receipt?.items || []).map((it) => ({
    invoice_id: it.invoice_id,
    invoice_number: it.invoice_number,
    customer_name: it.customer_name,
    customer_email: it.customer_email || '',
    balance_due: it.balance_due,
    due_date: it.due_date,
    days_overdue: it.days_overdue,
    tone: it.tone || 'first_time',
    personalized: !!it.personalized,
    is_new_client: !!it.is_new_client,
    has_recent_activity: !!it.has_recent_activity,
    last_note_present: !!it.last_note_present,
    relationship_age_days: it.relationship_age_days,
    last_invoice_days_ago: it.last_invoice_days_ago,
    subject: it.draft_subject,
    body: it.draft_body,
    send: !!it.can_send,
  }));

const TONE_BADGE = {
  first_time: { label: 'First-time', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' },
  occasional: { label: 'Occasional late', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' },
  chronic: { label: 'Chronic late', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300' },
};

export default function AgentTab({ workspaceId }) {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const wsId = workspaceId || currentWorkspace?.workspace_id;

  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null); // {skill_id, receipt}
  const [planLoading, setPlanLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [recentRuns, setRecentRuns] = useState([]);

  // P&L plan form (only relevant when activePlan.skill.id === 'pl_report')
  const [plPeriod, setPlPeriod] = useState('ytd');
  const [plRecipient, setPlRecipient] = useState('');
  const [showReviewQueue, setShowReviewQueue] = useState(false); // medium bucket
  const [showAskPanel, setShowAskPanel] = useState(false);       // low bucket
  const [accounts, setAccounts] = useState([]);

  // Chase AR state — editable per-invoice entries pulled from the plan.
  const [arEntries, setArEntries] = useState([]); // [{invoice_id, customer_email, subject, body, send}]

  // Skill chaining — when set on the Categorize receipt, after a
  // successful /execute we auto-launch this skill as the next step.
  // Currently the only supported chain is categorize → pl_report.
  const [chainTo, setChainTo] = useState(null); // null | 'pl_report'
  const [chainPeriod, setChainPeriod] = useState('ytd');

  // Reply Triage state — user pastes replies, then we classify them.
  const [triageInput, setTriageInput] = useState('');
  const [triageEntries, setTriageEntries] = useState([]); // [{reply_id, category, send_draft, sender_email, draft_subject, draft_reply, ...}]

  // Reconcile state — per-match approval flags.
  const [reconMatches, setReconMatches] = useState({ high: [], medium: [], low: [] });
  const [reconApproved, setReconApproved] = useState({}); // {transaction_id: true}

  // Preferences dialog state.
  const [showPrefs, setShowPrefs] = useState(false);
  const loadAccounts = async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/workspaces/${wsId}/accounts`);
      if (r.ok) setAccounts((await r.json()) || []);
    } catch (err) {
      void err; // non-critical
    }
  };
  useEffect(() => { loadAccounts(); }, [wsId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/agent/skills`);
        if (r.ok) setSkills((await r.json()).skills || []);
      } finally {
        setSkillsLoading(false);
      }
    })();
  }, []);

  const loadRecentRuns = async () => {
    if (!wsId) return;
    try {
      const r = await apiFetch(`${API_URL}/api/agent/log?workspace_id=${wsId}&limit=5`);
      if (r.ok) setRecentRuns((await r.json()).runs || []);
    } catch (err) {
      void err; // non-critical
    }
  };

  useEffect(() => { loadRecentRuns(); }, [wsId]);

  // Deep-link: ?launch=categorize|pl_report|chase_ar auto-opens that
  // skill's action receipt once we've fetched the skills catalog + wsId.
  // The Smart Suggestions banner on /banking, the email "View P&L" link,
  // and Dashboard widgets all use this to land users straight into a flow.
  const autoLaunchedRef = useRef(false);
  useEffect(() => {
    if (autoLaunchedRef.current) return;
    if (!wsId || skillsLoading || skills.length === 0) return;
    const target = searchParams.get('launch');
    if (!target) return;
    const skill = skills.find((s) => s.id === target);
    if (!skill) return;
    autoLaunchedRef.current = true;
    // Drop the param so a re-mount doesn't re-trigger.
    const next = new URLSearchParams(searchParams);
    next.delete('launch');
    setSearchParams(next, { replace: true });
    launchSkill(skill);
  }, [wsId, skillsLoading, skills, searchParams]);

  const launchSkill = async (skill) => {
    if (skill.status === 'coming_soon') {
      toast.info(`${skill.name} is coming soon — wired into the same Skills launcher.`);
      return;
    }
    if (skill.deep_link) {
      navigate(skill.deep_link);
      return;
    }
    if (skill.id === 'categorize') {
      setPlanLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/agent/skill/categorize/plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_id: wsId }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          if (r.status === 402) {
            toast.error('AI Agent requires a paid plan. Upgrade to Starter to enable skills.', {
              action: { label: 'Upgrade', onClick: () => navigate('/billing') },
            });
            return;
          }
          throw new Error(err.detail || 'Could not plan this skill.');
        }
        const receipt = await r.json();
        setActivePlan({ skill, receipt });
      } catch (e) {
        toast.error(e.message || 'Plan failed.');
      } finally {
        setPlanLoading(false);
      }
    }
    if (skill.id === 'pl_report') {
      setPlanLoading(true);
      setPlPeriod('ytd');
      try {
        const r = await apiFetch(`${API_URL}/api/agent/skill/pl_report/plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_id: wsId, period: 'ytd' }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          if (r.status === 402) {
            toast.error('AI Agent requires a paid plan.', {
              action: { label: 'Upgrade', onClick: () => navigate('/billing') },
            });
            return;
          }
          throw new Error(err.detail || 'Could not plan this skill.');
        }
        const receipt = await r.json();
        setPlRecipient(receipt.default_recipient || '');
        setActivePlan({ skill, receipt });
      } catch (e) {
        toast.error(e.message || 'Plan failed.');
      } finally {
        setPlanLoading(false);
      }
    }
    if (skill.id === 'chase_ar') {
      setPlanLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/agent/skill/chase_ar/plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_id: wsId, days_overdue_threshold: 1 }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          if (r.status === 402) {
            toast.error('AI Agent requires a paid plan.', {
              action: { label: 'Upgrade', onClick: () => navigate('/billing') },
            });
            return;
          }
          throw new Error(err.detail || 'Could not plan this skill.');
        }
        const receipt = await r.json();
        setArEntries(receiptToArEntries(receipt));
        setActivePlan({ skill, receipt });
      } catch (e) {
        toast.error(e.message || 'Plan failed.');
      } finally {
        setPlanLoading(false);
      }
    }

    if (skill.id === 'reconcile') {
      setPlanLoading(true);
      try {
        const r = await apiFetch(`${API_URL}/api/agent/skill/reconcile/plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workspace_id: wsId, lookback_days: 90 }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          if (r.status === 402) {
            toast.error('AI Agent requires a paid plan.', {
              action: { label: 'Upgrade', onClick: () => navigate('/billing') },
            });
            return;
          }
          throw new Error(err.detail || 'Could not plan reconcile.');
        }
        const receipt = await r.json();
        setReconMatches(receipt.buckets || { high: [], medium: [], low: [] });
        // Pre-approve every high-confidence match.
        const auto = {};
        (receipt.buckets?.high || []).forEach((m) => { auto[m.transaction_id] = true; });
        setReconApproved(auto);
        setActivePlan({ skill, receipt });
      } catch (e) {
        toast.error(e.message || 'Plan failed.');
      } finally {
        setPlanLoading(false);
      }
    }

    if (skill.id === 'reply_triage') {
      // Reply triage doesn't pre-fetch anything — user pastes first.
      setTriageInput('');
      setTriageEntries([]);
      setActivePlan({ skill, receipt: { ok: true, skill: 'reply_triage', items: [], bucket_counts: {}, awaiting_input: true } });
    }
  };

  // Reply Triage — parse the textarea + send to /plan.
  const runReplyTriage = async () => {
    const blocks = triageInput.split(/\n\s*---+\s*\n/).map((s) => s.trim()).filter(Boolean);
    if (blocks.length === 0) {
      toast.error('Paste at least one reply (separate multiple with "---" on its own line).');
      return;
    }
    const items = blocks.map((block, i) => {
      // Lightly parse "From: x@y / Subject: ...." headers if present.
      const lines = block.split('\n');
      let sender = '', subject = '';
      const bodyLines = [];
      let inHeaders = true;
      for (const line of lines) {
        if (inHeaders) {
          const fm = line.match(/^from:\s*(.+)$/i);
          const sm = line.match(/^subject:\s*(.+)$/i);
          if (fm) { sender = fm[1].trim(); continue; }
          if (sm) { subject = sm[1].trim(); continue; }
          if (line.trim() === '') { inHeaders = false; continue; }
          inHeaders = false;
        }
        bodyLines.push(line);
      }
      return {
        reply_id: `rep_${i}`,
        sender_email: sender || null,
        subject: subject || null,
        body: bodyLines.join('\n').trim() || block,
      };
    });
    setPlanLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/agent/skill/reply_triage/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, items }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Triage failed.');
      }
      const receipt = await r.json();
      setTriageEntries((receipt.items || []).map((it) => ({
        ...it,
        send_draft: !!it.draft_reply && it.category !== 'auto_archive',
      })));
      setActivePlan((cur) => cur ? { ...cur, receipt } : cur);
    } catch (e) {
      toast.error(e.message || 'Triage failed.');
    } finally {
      setPlanLoading(false);
    }
  };
  const personalizeChaseAR = async () => {
    if (!activePlan || activePlan.skill.id !== 'chase_ar') return;
    setPlanLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/agent/skill/chase_ar/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, days_overdue_threshold: 1, personalize: true }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Personalize failed.');
      }
      const receipt = await r.json();
      setArEntries(receiptToArEntries(receipt));
      setActivePlan((cur) => cur ? { ...cur, receipt } : cur);
      toast.success(
        receipt.personalized_count
          ? `AI Buck personalized ${receipt.personalized_count} reminder${receipt.personalized_count === 1 ? '' : 's'}.`
          : 'No personalization applied — falling back to templates.'
      );
    } catch (e) {
      toast.error(e.message || 'Personalize failed.');
    } finally {
      setPlanLoading(false);
    }
  };

  const replanPL = async (newPeriod) => {
    setPlPeriod(newPeriod);
    setPlanLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/agent/skill/pl_report/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, period: newPeriod }),
      });
      if (r.ok) {
        const receipt = await r.json();
        setActivePlan((cur) => cur ? { ...cur, receipt } : cur);
      }
    } finally {
      setPlanLoading(false);
    }
  };

  const executePlan = async () => {
    if (!activePlan) return;
    setExecuting(true);
    try {
      const sid = activePlan.skill.id;
      let url;
      let body;
      if (sid === 'pl_report') {
        url = `${API_URL}/api/agent/skill/pl_report/execute`;
        body = { workspace_id: wsId, period: plPeriod, recipient_email: plRecipient || null };
      } else if (sid === 'chase_ar') {
        const entries = arEntries
          .filter((e) => e.send && e.customer_email && e.customer_email.includes('@'))
          .map((e) => ({
            invoice_id: e.invoice_id,
            customer_email: e.customer_email,
            subject: e.subject,
            body: e.body,
          }));
        if (entries.length === 0) {
          toast.error('No reminders selected. Toggle "Send" on at least one row.');
          setExecuting(false);
          return;
        }
        url = `${API_URL}/api/agent/skill/chase_ar/execute`;
        body = { workspace_id: wsId, entries };
      } else if (sid === 'reconcile') {
        const entries = [...reconMatches.high, ...reconMatches.medium, ...reconMatches.low]
          .filter((m) => reconApproved[m.transaction_id])
          .map((m) => ({
            transaction_id: m.transaction_id,
            kind: m.kind || 'invoice',
            target_id: m.target_id || m.invoice_id,
          }));
        if (entries.length === 0) {
          toast.error('Approve at least one match before executing.');
          setExecuting(false);
          return;
        }
        url = `${API_URL}/api/agent/skill/reconcile/execute`;
        body = { workspace_id: wsId, entries };
      } else if (sid === 'reply_triage') {
        const entries = triageEntries.map((e) => ({
          reply_id: e.reply_id,
          category: e.category,
          send_draft: !!e.send_draft && !!e.draft_reply,
          sender_email: e.sender_email || null,
          draft_subject: e.subject ? `Re: ${e.subject.replace(/^re:\s*/i, '')}` : 'Re: your message',
          draft_reply: e.draft_reply || null,
        }));
        if (entries.length === 0) {
          toast.error('Triage some replies first.');
          setExecuting(false);
          return;
        }
        url = `${API_URL}/api/agent/skill/reply_triage/execute`;
        body = { workspace_id: wsId, entries };
      } else {
        url = `${API_URL}/api/agent/skill/categorize/execute`;
        body = { workspace_id: wsId, create_rules_from_high_confidence: true };
      }
      const r = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not execute.');
      }
      const result = await r.json();
      if (sid === 'chase_ar') {
        toast.success(`Sent ${result.sent} reminder${result.sent === 1 ? '' : 's'}.`);
      } else if (sid === 'reconcile') {
        toast.success(`Reconciled ${result.applied} bank deposit${result.applied === 1 ? '' : 's'}.`, {
          action: result.run_id ? {
            label: 'View log',
            onClick: () => navigate(`/cfo-buck?tab=agent&run=${result.run_id}`),
          } : undefined,
        });
      } else if (sid === 'reply_triage') {
        toast.success(
          `Triaged · ${result.sent} replies sent · ${result.flagged} flagged for you · ${result.archived} archived.`
        );
      } else {
        toast.success(result.message || 'Applied.', {
          action: result.run_id ? {
            label: 'View log',
            onClick: () => navigate(`/cfo-buck?tab=agent&run=${result.run_id}`),
          } : undefined,
        });
      }
      setActivePlan(null);
      setArEntries([]);
      setReconMatches({ high: [], medium: [], low: [] });
      setReconApproved({});
      setTriageEntries([]);
      setTriageInput('');
      loadRecentRuns();

      // Skill chaining — Categorize was the only chain source for now.
      // After a successful categorize/execute, if the user opted to chain
      // a P&L send, immediately launch the pl_report flow with their
      // chosen period. The chain target is fetched from skills catalog
      // by id so it gets the right metadata (paywall, color, etc.).
      if (sid === 'categorize' && chainTo === 'pl_report') {
        const chainSkill = skills.find((s) => s.id === 'pl_report');
        const chainedPeriod = chainPeriod;
        setChainTo(null);
        if (chainSkill) {
          // Small delay so the categorize success toast lands first.
          setTimeout(async () => {
            setPlanLoading(true);
            setPlPeriod(chainedPeriod);
            try {
              const r = await apiFetch(`${API_URL}/api/agent/skill/pl_report/plan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ workspace_id: wsId, period: chainedPeriod }),
              });
              if (r.ok) {
                const receipt = await r.json();
                setPlRecipient(receipt.default_recipient || '');
                setActivePlan({ skill: chainSkill, receipt });
                toast.message('Now P&L — review & send', {
                  description: 'AI Buck chained the P&L step. Adjust the period or recipient before sending.',
                });
              }
            } finally {
              setPlanLoading(false);
            }
          }, 600);
        }
      }
    } catch (e) {
      toast.error(e.message || 'Execute failed.');
    } finally {
      setExecuting(false);
    }
  };

  // Single-suggestion apply — called by both medium swipe + low inline ask.
  const applyOne = async (suggestionId, opts = {}) => {
    const { decision = 'approve', overrideAccountId = null, createRule = false } = opts;
    const r = await apiFetch(`${API_URL}/api/agent/skill/categorize/apply-one`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        suggestion_id: suggestionId,
        workspace_id: wsId,
        override_account_id: overrideAccountId,
        create_rule: createRule,
        decision,
      }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || 'Apply failed.');
    }
    return r.json();
  };

  const undoRun = async (runId) => {
    try {
      const r = await apiFetch(`${API_URL}/api/agent/log/${runId}/undo`, {
        method: 'POST',
      });
      if (!r.ok) throw new Error('Undo failed.');
      const result = await r.json();
      toast.success(`Reverted ${result.reverted} item${result.reverted === 1 ? '' : 's'}.`);
      loadRecentRuns();
    } catch (e) {
      toast.error(e.message || 'Undo failed.');
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-950/40 flex items-center justify-center">
            <Wand2 className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">AI Buck Skills</h2>
            <p className="text-sm text-muted-foreground">
              One agent. Pick a skill — AI Buck plans the work, you approve, and it executes.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowPrefs(true)}
          data-testid="agent-prefs-btn"
        >
          <Settings className="w-4 h-4 mr-1.5" />
          Preferences
        </Button>
      </div>

      {/* Skills launcher */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="agent-skills-grid">
        {skillsLoading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading skills…
          </div>
        ) : (
          skills.map((skill) => {
            const Icon = ICONS[skill.icon] || Sparkles;
            const c = COLOR_STYLES[skill.color] || COLOR_STYLES.violet;
            const disabled = skill.status === 'coming_soon';
            return (
              <button
                key={skill.id}
                type="button"
                onClick={() => launchSkill(skill)}
                disabled={planLoading && activePlan?.skill?.id === skill.id}
                className={cn(
                  'text-left p-4 rounded-xl border-2 transition-all relative group',
                  c.bg, c.border,
                  disabled ? 'opacity-60 cursor-default' : 'hover:scale-[1.02] hover:shadow-md cursor-pointer'
                )}
                data-testid={`skill-card-${skill.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white', c.accent)}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {disabled && (
                    <Badge variant="secondary" className="text-[10px]">Soon</Badge>
                  )}
                  {!disabled && planLoading && activePlan?.skill?.id === skill.id && (
                    <Loader2 className="w-4 h-4 animate-spin opacity-60" />
                  )}
                </div>
                <div className={cn('font-semibold text-sm mb-1', c.text)}>
                  {skill.name}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-2">
                  {skill.tagline}
                </div>
                {!disabled && (
                  <ArrowRight className={cn(
                    'w-4 h-4 mt-3 transition-transform group-hover:translate-x-1',
                    c.text,
                  )} />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Recent runs / audit log */}
      <Card data-testid="agent-recent-runs">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent agent runs</CardTitle>
          <CardDescription>
            Every agent action is logged here — undo within 30 days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentRuns.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-4 text-center bg-muted/30 rounded-lg">
              No runs yet. Pick a skill above to get started.
            </div>
          ) : (
            <div className="space-y-1.5">
              {recentRuns.map((run) => (
                <div
                  key={run.run_id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-card"
                  data-testid={`agent-run-${run.run_id}`}
                >
                  <CheckCircle2 className={cn('w-4 h-4 shrink-0',
                    run.undone_at ? 'text-muted-foreground' : 'text-emerald-600')} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {run.skill === 'categorize' ? 'Categorize transactions'
                        : run.skill === 'pl_report' ? 'P&L report email'
                        : run.skill === 'chase_ar' ? 'Chase overdue invoices'
                        : run.skill === 'reconcile' ? 'Reconcile bank deposits'
                        : run.skill === 'reply_triage' ? 'Triage customer replies'
                        : run.skill}
                      {run.undone_at && <span className="text-muted-foreground"> · Undone</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Applied {run.applied_count}{run.skipped_count ? ` · Skipped ${run.skipped_count}` : ''}
                      {' · '}
                      {new Date(run.started_at).toLocaleString()}
                    </div>
                  </div>
                  {!run.undone_at && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => undoRun(run.run_id)}
                      data-testid={`agent-undo-${run.run_id}`}
                    >
                      <Undo2 className="w-3.5 h-3.5 mr-1" />
                      Undo
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Receipt modal */}
      <Dialog open={!!activePlan} onOpenChange={(o) => !o && setActivePlan(null)}>
        <DialogContent className="max-w-xl" data-testid="action-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-violet-600" />
              {activePlan?.skill?.name} — Action Receipt
            </DialogTitle>
            <DialogDescription>
              Review what AI Buck will do. Nothing is applied until you approve.
            </DialogDescription>
          </DialogHeader>

          {activePlan?.receipt && activePlan.skill.id === 'categorize' && (
            <div className="space-y-4">
              {/* Summary line */}
              <div className="text-sm font-medium bg-violet-50 dark:bg-violet-950/30 rounded-lg p-3" data-testid="receipt-summary">
                {activePlan.receipt.summary}
              </div>

              {/* Buckets */}
              <div className="grid grid-cols-3 gap-2">
                <BucketStat
                  label="Ready to apply"
                  count={activePlan.receipt.buckets?.high?.length || 0}
                  icon={CheckCircle2}
                  color="emerald"
                  testid="bucket-high"
                />
                <BucketStat
                  label="Flag for review"
                  count={activePlan.receipt.buckets?.medium?.length || 0}
                  icon={AlertTriangle}
                  color="amber"
                  testid="bucket-medium"
                  onClick={
                    (activePlan.receipt.buckets?.medium?.length || 0) > 0
                      ? () => setShowReviewQueue(true)
                      : undefined
                  }
                />
                <BucketStat
                  label="Need your input"
                  count={activePlan.receipt.buckets?.low?.length || 0}
                  icon={HelpCircle}
                  color="rose"
                  testid="bucket-low"
                  onClick={
                    (activePlan.receipt.buckets?.low?.length || 0) > 0
                      ? () => setShowAskPanel(true)
                      : undefined
                  }
                />
              </div>

              {/* Impact rollup */}
              {(activePlan.receipt.impact_by_account || []).length > 0 && (
                <div className="border rounded-lg overflow-hidden" data-testid="receipt-impact">
                  <div className="px-3 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Estimated impact (high-confidence batch)
                  </div>
                  <div className="divide-y">
                    {activePlan.receipt.impact_by_account.slice(0, 6).map((row) => (
                      <div key={row.account_id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <div className="truncate">
                          <span className="font-medium">{row.account_name}</span>
                          <span className="text-muted-foreground"> · {row.txn_count} txns</span>
                        </div>
                        <div className="font-mono text-sm shrink-0">
                          {fmtUSD(row.total_amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sample preview rows */}
              {(activePlan.receipt.buckets?.high || []).slice(0, 3).length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Example:
                  {activePlan.receipt.buckets.high.slice(0, 3).map((it) => (
                    <div key={it.suggestion_id} className="ml-2 mt-0.5">
                      <span className="text-foreground">{it.txn_name}</span>
                      {' → '}
                      <span className="font-medium">{it.suggested_account_name}</span>
                      <span className="opacity-60"> ({Math.round((it.confidence || 0) * 100)}%)</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Skill chaining — "and then" UX. After this Categorize
                  run lands, AI Buck can automatically launch the P&L
                  receipt with the user's preferred period. The user
                  still confirms the P&L send. */}
              <div className="rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/20 p-3 space-y-2" data-testid="chain-toggle">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={chainTo === 'pl_report'}
                    onChange={(e) => setChainTo(e.target.checked ? 'pl_report' : null)}
                    data-testid="chain-toggle-checkbox"
                  />
                  <span>
                    <span className="font-medium">…then email me a P&amp;L when this is done</span>
                    <span className="block text-xs text-muted-foreground">
                      AI Buck will chain the P&amp;L step automatically. You&apos;ll review the period &amp; recipient before it sends.
                    </span>
                  </span>
                </label>
                {chainTo === 'pl_report' && (
                  <div className="flex items-center gap-1.5 flex-wrap pl-6">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1">Period:</span>
                    {[
                      {v:'this_month', l:'This month'},
                      {v:'last_month', l:'Last month'},
                      {v:'ytd', l:'YTD'},
                      {v:'q1', l:'Q1'}, {v:'q2', l:'Q2'}, {v:'q3', l:'Q3'}, {v:'q4', l:'Q4'},
                    ].map(p => (
                      <button
                        key={p.v}
                        type="button"
                        onClick={() => setChainPeriod(p.v)}
                        className={cn(
                          'px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors',
                          chainPeriod === p.v
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-card border-border hover:bg-muted/50 text-muted-foreground'
                        )}
                        data-testid={`chain-period-${p.v}`}
                      >
                        {p.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activePlan?.receipt && activePlan.skill.id === 'pl_report' && (
            <div className="space-y-4" data-testid="pl-receipt">
              {/* Period selector */}
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                  Period
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    {v:'this_month', l:'This month'},
                    {v:'last_month', l:'Last month'},
                    {v:'q1', l:'Q1'},  {v:'q2', l:'Q2'},  {v:'q3', l:'Q3'},  {v:'q4', l:'Q4'},
                    {v:'ytd', l:'YTD'},
                    {v:'last_year', l:'Last year'},
                  ].map(p => (
                    <button
                      key={p.v}
                      type="button"
                      onClick={() => replanPL(p.v)}
                      disabled={planLoading}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors',
                        plPeriod === p.v
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-card border-border hover:bg-muted/50 text-muted-foreground'
                      )}
                      data-testid={`pl-period-${p.v}`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Headline */}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg border bg-card p-3 text-center" data-testid="pl-revenue">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Revenue</div>
                  <div className="text-xl font-bold text-foreground">{fmtUSD(activePlan.receipt.revenue_total)}</div>
                </div>
                <div className="rounded-lg border bg-card p-3 text-center" data-testid="pl-expenses">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Expenses</div>
                  <div className="text-xl font-bold text-foreground">{fmtUSD(activePlan.receipt.expense_total)}</div>
                </div>
                <div className={cn(
                  'rounded-lg border-2 p-3 text-center',
                  activePlan.receipt.net_income >= 0
                    ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'
                    : 'border-rose-300 bg-rose-50 dark:bg-rose-950/30'
                )} data-testid="pl-net">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Net</div>
                  <div className={cn('text-xl font-bold',
                    activePlan.receipt.net_income >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'
                  )}>
                    {activePlan.receipt.net_income >= 0 ? '+' : ''}{fmtUSD(activePlan.receipt.net_income)}
                  </div>
                </div>
              </div>

              {/* Top accounts */}
              {activePlan.receipt.top_accounts?.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Top accounts
                  </div>
                  <div className="divide-y max-h-48 overflow-y-auto">
                    {activePlan.receipt.top_accounts.map((row) => (
                      <div key={row.account_id} className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <div className="truncate">
                          <span className="font-medium">{row.account_name}</span>
                          <span className="text-muted-foreground"> · {row.type}</span>
                        </div>
                        <div className={cn('font-mono shrink-0',
                          row.type === 'income' ? 'text-emerald-700 dark:text-emerald-400' : 'text-foreground'
                        )}>
                          {row.type === 'income' ? '+' : '-'}{fmtUSD(Math.abs(row.amount))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recipient */}
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground" htmlFor="pl-recipient">
                  Email to
                </label>
                <input
                  id="pl-recipient"
                  type="email"
                  value={plRecipient}
                  onChange={(e) => setPlRecipient(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                  placeholder="you@yourfirm.com"
                  data-testid="pl-recipient"
                />
              </div>
            </div>
          )}

          {activePlan?.receipt && activePlan.skill.id === 'chase_ar' && (
            <div className="space-y-3" data-testid="chase-ar-receipt">
              {/* Summary */}
              <div className="text-sm font-medium bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="chase-ar-summary">
                <span>{activePlan.receipt.summary}</span>
                <Badge variant="outline" className="shrink-0">
                  {fmtUSD(activePlan.receipt.total_open_amount)}
                </Badge>
              </div>

              {/* Personalize CTA — runs the same /plan with personalize=true.
                  AI Buck reads each customer's paid-late history and
                  rewrites the tone/wording per row. One Claude call,
                  ~3-5s. Disables once at least one row is personalized. */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={personalizeChaseAR}
                disabled={planLoading || (activePlan.receipt.personalized_count || 0) >= arEntries.length}
                className="w-full justify-center border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-950/30"
                data-testid="chase-ar-personalize-btn"
              >
                {planLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Personalizing…</>
                ) : (activePlan.receipt.personalized_count || 0) > 0 ? (
                  <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Personalized · {activePlan.receipt.personalized_count} rewritten</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Personalize with AI (tone-aware by payment history)</>
                )}
              </Button>

              {activePlan.receipt.no_email_count > 0 && (
                <div className="text-xs text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/20 rounded-md p-2 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {activePlan.receipt.no_email_count} customer(s) are missing an email — those rows are skipped.
                </div>
              )}

              {arEntries.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  Nothing overdue. AI Buck has nothing to chase.
                </div>
              ) : (
                <div className="border rounded-lg divide-y max-h-[360px] overflow-y-auto" data-testid="chase-ar-list">
                  {arEntries.map((e, idx) => (
                    <div key={e.invoice_id} className="p-3 space-y-2" data-testid={`chase-ar-row-${idx}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-sm font-semibold truncate">
                              {e.customer_name}
                              <span className="text-muted-foreground font-normal ml-2">#{e.invoice_number}</span>
                            </span>
                            {e.personalized && (
                              <Badge
                                variant="secondary"
                                className={cn('text-[10px] px-1.5 py-0', TONE_BADGE[e.tone || 'first_time']?.cls)}
                                data-testid={`chase-ar-tone-${idx}`}
                              >
                                {TONE_BADGE[e.tone || 'first_time']?.label}
                              </Badge>
                            )}
                            {/* Context badges layered on top of payment tone.
                                These signal that AI Buck softened/adjusted
                                the tone based on relationship signals, not
                                just late count. */}
                            {e.personalized && e.is_new_client && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 border-violet-300 text-violet-700 dark:text-violet-300"
                                data-testid={`chase-ar-context-new-${idx}`}
                                title={e.relationship_age_days != null ? `Client for ${e.relationship_age_days} day${e.relationship_age_days === 1 ? '' : 's'}` : 'New client'}
                              >
                                New client
                              </Badge>
                            )}
                            {e.personalized && e.has_recent_activity && !e.is_new_client && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-700 dark:text-blue-300"
                                data-testid={`chase-ar-context-active-${idx}`}
                                title={e.last_invoice_days_ago != null ? `Last invoice ${e.last_invoice_days_ago} day${e.last_invoice_days_ago === 1 ? '' : 's'} ago` : 'Active relationship'}
                              >
                                Active
                              </Badge>
                            )}
                            {e.personalized && e.last_note_present && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 border-fuchsia-300 text-fuchsia-700 dark:text-fuchsia-300"
                                data-testid={`chase-ar-context-note-${idx}`}
                                title="AI Buck factored your private note into the tone"
                              >
                                Note on file
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {fmtUSD(e.balance_due)} · {e.days_overdue} day{e.days_overdue === 1 ? '' : 's'} overdue
                          </div>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={e.send && !!e.customer_email}
                            disabled={!e.customer_email}
                            onChange={(ev) => {
                              setArEntries((cur) => {
                                const next = [...cur];
                                next[idx] = { ...next[idx], send: ev.target.checked };
                                return next;
                              });
                            }}
                            data-testid={`chase-ar-send-${idx}`}
                          />
                          Send
                        </label>
                      </div>
                      <input
                        type="email"
                        value={e.customer_email}
                        placeholder="customer@email.com"
                        onChange={(ev) => {
                          setArEntries((cur) => {
                            const next = [...cur];
                            next[idx] = { ...next[idx], customer_email: ev.target.value };
                            return next;
                          });
                        }}
                        className="w-full px-2.5 py-1.5 rounded-md border bg-background text-xs"
                        data-testid={`chase-ar-email-${idx}`}
                      />
                      <input
                        type="text"
                        value={e.subject}
                        onChange={(ev) => {
                          setArEntries((cur) => {
                            const next = [...cur];
                            next[idx] = { ...next[idx], subject: ev.target.value };
                            return next;
                          });
                        }}
                        className="w-full px-2.5 py-1.5 rounded-md border bg-background text-xs font-medium"
                        data-testid={`chase-ar-subject-${idx}`}
                      />
                      <textarea
                        rows={3}
                        value={e.body}
                        onChange={(ev) => {
                          setArEntries((cur) => {
                            const next = [...cur];
                            next[idx] = { ...next[idx], body: ev.target.value };
                            return next;
                          });
                        }}
                        className="w-full px-2.5 py-1.5 rounded-md border bg-background text-xs"
                        data-testid={`chase-ar-body-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activePlan?.receipt && activePlan.skill.id === 'reconcile' && (
            <div className="space-y-3" data-testid="reconcile-receipt">
              <div className="text-sm font-medium bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="reconcile-summary">
                <span>{activePlan.receipt.summary}</span>
                <Badge variant="outline" className="shrink-0 whitespace-nowrap">
                  {activePlan.receipt.deposits_scanned}↓ · {activePlan.receipt.withdrawals_scanned || 0}↑
                  {' · '}
                  {activePlan.receipt.open_invoice_count} inv · {activePlan.receipt.open_bill_count || 0} bill
                </Badge>
              </div>

              {(['high', 'medium', 'low']).map((bucket) => {
                const rows = reconMatches[bucket] || [];
                if (rows.length === 0) return null;
                const meta = {
                  high:   { label: 'Ready to apply',  desc: 'Exact amount, within 7 days, name matched.', tone: 'emerald' },
                  medium: { label: 'Worth reviewing', desc: 'Amount + date close, but name didn’t match.', tone: 'amber' },
                  low:    { label: 'Uncertain',       desc: 'Amount only — likely needs your eye.',         tone: 'rose' },
                }[bucket];
                return (
                  <div key={bucket} className="border rounded-lg overflow-hidden" data-testid={`reconcile-bucket-${bucket}`}>
                    <div className={cn(
                      'px-3 py-2 flex items-center justify-between text-xs font-semibold',
                      meta.tone === 'emerald' && 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-300',
                      meta.tone === 'amber' && 'bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-300',
                      meta.tone === 'rose' && 'bg-rose-50 dark:bg-rose-950/20 text-rose-800 dark:text-rose-300',
                    )}>
                      <div>
                        <div>{meta.label} · {rows.length}</div>
                        <div className="text-[10px] font-normal opacity-80">{meta.desc}</div>
                      </div>
                    </div>
                    <div className="divide-y max-h-[240px] overflow-y-auto">
                      {rows.map((m) => {
                        const isBill = m.kind === 'bill';
                        return (
                          <label key={m.transaction_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30">
                            <input
                              type="checkbox"
                              checked={!!reconApproved[m.transaction_id]}
                              onChange={(e) => setReconApproved((cur) => ({ ...cur, [m.transaction_id]: e.target.checked }))}
                              data-testid={`reconcile-approve-${m.transaction_id}`}
                            />
                            <div className="flex-1 min-w-0 grid grid-cols-2 gap-2 items-center">
                              <div className="min-w-0">
                                <div className="text-xs text-muted-foreground truncate">
                                  {m.txn_date} · {isBill ? 'Bank withdrawal' : 'Bank deposit'}
                                </div>
                                <div className="text-sm font-medium truncate">{m.txn_name || (isBill ? 'Withdrawal' : 'Deposit')}</div>
                              </div>
                              <div className="min-w-0 flex items-center gap-2">
                                <Link2 className={cn('w-3 h-3 shrink-0', isBill ? 'text-amber-500' : 'text-muted-foreground')} />
                                <div className="min-w-0">
                                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                    <Badge variant="secondary" className={cn('text-[9px] px-1 py-0', isBill
                                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                      : 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                                    )} data-testid={`reconcile-kind-${m.transaction_id}`}>
                                      {isBill ? 'BILL' : 'INV'}
                                    </Badge>
                                    <span className="truncate">{m.counterparty_name || m.customer_name || m.target_label} · {m.target_date || m.invoice_date || ''}</span>
                                  </div>
                                  <div className="text-sm font-medium truncate">#{m.target_number || m.invoice_number}</div>
                                </div>
                              </div>
                            </div>
                            <div className="text-sm font-mono shrink-0">{fmtUSD(m.txn_amount)}</div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {reconMatches.high.length === 0 && reconMatches.medium.length === 0 && reconMatches.low.length === 0 && (
                <div className="py-8 text-center text-sm text-muted-foreground" data-testid="reconcile-empty">
                  <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  No unmatched bank lines — books are clean.
                </div>
              )}
            </div>
          )}

          {activePlan?.receipt && activePlan.skill.id === 'reply_triage' && (
            <div className="space-y-3" data-testid="triage-receipt">
              {triageEntries.length === 0 ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Paste one or more customer reply emails below. Separate multiple replies with a line of <code className="text-xs bg-muted px-1 rounded">---</code>.
                    AI Buck will classify each as <em>paying soon</em>, <em>wants payment plan</em>, <em>disputes</em>, <em>needs you</em>, or <em>auto-archive</em>, and draft one-click acknowledgements where appropriate.
                  </p>
                  <textarea
                    rows={9}
                    value={triageInput}
                    onChange={(e) => setTriageInput(e.target.value)}
                    placeholder={"From: customer@example.com\nSubject: Re: Invoice 105\n\nJust sent the ACH this morning — should clear in 1-2 days.\n\n---\n\nFrom: out@office.com\nSubject: OOO\n\nI'm out until Monday."}
                    className="w-full px-3 py-2 rounded-md border bg-background text-xs font-mono"
                    data-testid="triage-input"
                  />
                  <Button
                    type="button"
                    onClick={runReplyTriage}
                    disabled={planLoading || !triageInput.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="triage-run-btn"
                  >
                    {planLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Triaging…</>
                      : <><Sparkles className="w-4 h-4 mr-2" /> Triage these replies</>}
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="triage-summary">
                    <span>{activePlan.receipt.summary}</span>
                    <Badge variant="outline" className="shrink-0">
                      {triageEntries.length} replies
                    </Badge>
                  </div>
                  <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                    {triageEntries.map((e, idx) => {
                      const catCls = {
                        paying_soon: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
                        payment_plan: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
                        disputes: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
                        needs_human: 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
                        auto_archive: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
                      }[e.category];
                      return (
                        <div key={e.reply_id} className="p-3 space-y-2" data-testid={`triage-row-${idx}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge variant="secondary" className={cn('text-[10px] px-1.5 py-0', catCls)} data-testid={`triage-cat-${idx}`}>
                                  {e.category_label}
                                </Badge>
                                <span className="text-[10px] text-muted-foreground">{Math.round((e.confidence || 0) * 100)}% confidence</span>
                              </div>
                              <div className="text-sm font-medium mt-1 truncate">{e.sender_email || '(no sender)'}</div>
                              <div className="text-xs text-muted-foreground truncate">{e.subject || '(no subject)'}</div>
                              {e.summary && <div className="text-xs italic text-muted-foreground mt-1">“{e.summary}”</div>}
                            </div>
                          </div>
                          {e.draft_reply && (
                            <div className="rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/20 p-2 space-y-1.5">
                              <label className="flex items-center gap-1.5 text-[11px] font-medium cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!e.send_draft}
                                  onChange={(ev) => {
                                    setTriageEntries((cur) => {
                                      const next = [...cur];
                                      next[idx] = { ...next[idx], send_draft: ev.target.checked };
                                      return next;
                                    });
                                  }}
                                  data-testid={`triage-send-${idx}`}
                                />
                                <MessageSquare className="w-3 h-3" />
                                Send this acknowledgement
                              </label>
                              <textarea
                                rows={3}
                                value={e.draft_reply}
                                onChange={(ev) => {
                                  setTriageEntries((cur) => {
                                    const next = [...cur];
                                    next[idx] = { ...next[idx], draft_reply: ev.target.value };
                                    return next;
                                  });
                                }}
                                className="w-full px-2 py-1.5 rounded-md border bg-background text-xs"
                                data-testid={`triage-draft-${idx}`}
                              />
                            </div>
                          )}
                          {e.suggested_action && (
                            <div className="text-[11px] text-muted-foreground">
                              <span className="font-medium">Next step:</span> {e.suggested_action}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}



          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActivePlan(null)} disabled={executing}>
              {activePlan?.skill?.id === 'reply_triage' && triageEntries.length === 0 ? 'Close' : 'Cancel'}
            </Button>
            {/* Hide the execute button entirely while reply_triage is
                waiting for user input — the dedicated "Triage these
                replies" button inside the receipt handles the LLM call.
                Once classified, the execute button takes over. */}
            {!(activePlan?.skill?.id === 'reply_triage' && triageEntries.length === 0) && (() => {
              const sid = activePlan?.skill?.id;
              const reconCount = sid === 'reconcile'
                ? Object.values(reconApproved).filter(Boolean).length
                : 0;
              const triageActionable = sid === 'reply_triage'
                ? triageEntries.length
                : 0;
              const disabled = (
                executing ||
                (sid === 'categorize' && !(activePlan?.receipt?.buckets?.high?.length)) ||
                (sid === 'pl_report' && !plRecipient) ||
                (sid === 'chase_ar' && !arEntries.some((e) => e.send && e.customer_email)) ||
                (sid === 'reconcile' && reconCount === 0) ||
                (sid === 'reply_triage' && triageActionable === 0)
              );
              const color =
                sid === 'pl_report' ? 'bg-blue-600 hover:bg-blue-700'
                : sid === 'chase_ar' ? 'bg-amber-600 hover:bg-amber-700'
                : sid === 'reconcile' ? 'bg-emerald-600 hover:bg-emerald-700'
                : sid === 'reply_triage' ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-violet-600 hover:bg-violet-700';
              const icon =
                sid === 'chase_ar' ? <Send className="w-4 h-4 mr-2" />
                : sid === 'reconcile' ? <Link2 className="w-4 h-4 mr-2" />
                : sid === 'reply_triage' ? <Inbox className="w-4 h-4 mr-2" />
                : <Wand2 className="w-4 h-4 mr-2" />;
              const label =
                sid === 'pl_report' ? `Send to ${plRecipient || '…'}`
                : sid === 'chase_ar' ? `Send ${arEntries.filter((e) => e.send && e.customer_email).length} reminder${arEntries.filter((e) => e.send && e.customer_email).length === 1 ? '' : 's'}`
                : sid === 'reconcile' ? `Apply ${reconCount} match${reconCount === 1 ? '' : 'es'}`
                : sid === 'reply_triage' ? `Confirm ${triageActionable} decision${triageActionable === 1 ? '' : 's'}`
                : `Approve & apply ${activePlan?.receipt?.buckets?.high?.length || 0}`;
              return (
                <Button
                  onClick={executePlan}
                  disabled={disabled}
                  className={color}
                  data-testid="receipt-execute-btn"
                >
                  {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : icon}
                  {label}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preferences dialog — confidence thresholds + automation toggles. */}
      <PreferencesDialog
        open={showPrefs}
        onClose={() => setShowPrefs(false)}
        workspaceId={wsId}
      />

      {/* Medium-bucket review queue — Tinder-style swipe surface */}
      <ReviewQueueDialog
        open={showReviewQueue}
        onClose={() => setShowReviewQueue(false)}
        items={activePlan?.receipt?.buckets?.medium || []}
        accounts={accounts}
        applyOne={applyOne}
        onItemHandled={(suggestionId) => {
          // Optimistically remove from the medium bucket so the count
          // decrements and the queue advances visually.
          setActivePlan((cur) => {
            if (!cur) return cur;
            const med = (cur.receipt.buckets.medium || []).filter(it => it.suggestion_id !== suggestionId);
            return { ...cur, receipt: { ...cur.receipt, buckets: { ...cur.receipt.buckets, medium: med } } };
          });
        }}
      />

      {/* Low-bucket inline "ask me about it" panel */}
      <AskPanelDialog
        open={showAskPanel}
        onClose={() => setShowAskPanel(false)}
        items={activePlan?.receipt?.buckets?.low || []}
        accounts={accounts}
        applyOne={applyOne}
        onItemHandled={(suggestionId) => {
          setActivePlan((cur) => {
            if (!cur) return cur;
            const low = (cur.receipt.buckets.low || []).filter(it => it.suggestion_id !== suggestionId);
            return { ...cur, receipt: { ...cur.receipt, buckets: { ...cur.receipt.buckets, low } } };
          });
        }}
      />
    </div>
  );
}

const BucketStat = ({ label, count, icon: Icon, color, testid, onClick }) => {
  const c = COLOR_STYLES[color] || COLOR_STYLES.violet;
  const clickable = !!onClick && count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={cn(
        'rounded-lg border-2 p-3 text-center w-full transition-all',
        c.bg, c.border,
        clickable && 'hover:scale-[1.03] hover:shadow-md cursor-pointer',
        !clickable && 'cursor-default',
      )}
      data-testid={testid}
    >
      <Icon className={cn('w-5 h-5 mx-auto mb-1', c.text)} />
      <div className={cn('text-2xl font-bold', c.text)}>{count}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">
        {label}{clickable && ' →'}
      </div>
    </button>
  );
};

// ─── Review Queue (medium bucket) ─────────────────────────────────────
// Big card stack. User taps Approve / Change / Skip per item. Each call
// hits /apply-one. Auto-advances; closes when queue is empty.

const ReviewQueueDialog = ({ open, onClose, items, accounts, applyOne, onItemHandled }) => {
  const [idx, setIdx] = useState(0);
  const [changeMode, setChangeMode] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setIdx(0); setChangeMode(false); }, [open]);

  if (!open) return null;
  const current = items[idx];

  const next = () => {
    setChangeMode(false);
    if (idx + 1 >= items.length) onClose();
    else setIdx(idx + 1);
  };

  const handle = async (decision, overrideAccountId = null) => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await applyOne(current.suggestion_id, {
        decision,
        overrideAccountId,
        createRule: decision === 'approve',
      });
      toast.success(decision === 'approve' ? 'Applied.' : decision === 'reject' ? 'Skipped.' : 'Snoozed.');
      onItemHandled(current.suggestion_id);
      next();
    } catch (e) {
      toast.error(e.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="review-queue-dialog">
        <DialogHeader>
          <DialogTitle>Review queue</DialogTitle>
          <DialogDescription>
            {items.length === 0
              ? 'All done — nothing left to review.'
              : `${idx + 1} of ${items.length} · approve, change the account, or skip`}
          </DialogDescription>
        </DialogHeader>

        {current ? (
          <div className="space-y-3" data-testid="review-queue-card">
            <div className="rounded-xl border-2 border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Transaction</div>
              <div className="text-lg font-semibold">{current.txn_name}</div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {current.txn_date} · {fmtUSD(current.txn_amount)}
              </div>
              <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-900">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">AI Buck suggests</div>
                <div className="text-base font-semibold text-amber-800 dark:text-amber-200">
                  {current.suggested_account_name}
                  <span className="text-xs ml-2 opacity-70">
                    ({Math.round((current.confidence || 0) * 100)}% confidence)
                  </span>
                </div>
                {current.rationale && (
                  <div className="text-xs text-muted-foreground italic mt-1">&ldquo;{current.rationale}&rdquo;</div>
                )}
              </div>
            </div>

            {changeMode ? (
              <div className="space-y-2" data-testid="review-change-panel">
                <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                  Pick a different account
                </label>
                <select
                  className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) handle('approve', e.target.value);
                  }}
                  data-testid="review-account-select"
                >
                  <option value="" disabled>Choose…</option>
                  {accounts.map((a) => (
                    <option key={a.account_id} value={a.account_id}>
                      {a.name} ({a.type})
                    </option>
                  ))}
                </select>
                <Button variant="ghost" size="sm" onClick={() => setChangeMode(false)}>Back</Button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => handle('reject')}
                  data-testid="review-skip-btn"
                >
                  Skip
                </Button>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => setChangeMode(true)}
                  data-testid="review-change-btn"
                >
                  Change
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={busy}
                  onClick={() => handle('approve')}
                  data-testid="review-approve-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Approve'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <div className="font-semibold">Queue cleared</div>
            <div className="text-xs text-muted-foreground mt-1">All medium-confidence items reviewed.</div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Ask Panel (low bucket) ───────────────────────────────────────────
// Conversational "AI Buck doesn't know — tell me which account" panel.
// Same /apply-one endpoint, but always opens in change-mode so the user
// picks the account explicitly. Optionally creates a rule from the answer.

const AskPanelDialog = ({ open, onClose, items, accounts, applyOne, onItemHandled }) => {
  const [idx, setIdx] = useState(0);
  const [createRule, setCreateRule] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setIdx(0); setCreateRule(true); }, [open]);
  if (!open) return null;
  const current = items[idx];
  const next = () => {
    if (idx + 1 >= items.length) onClose();
    else setIdx(idx + 1);
  };
  const teach = async (accountId) => {
    if (!current || busy) return;
    setBusy(true);
    try {
      await applyOne(current.suggestion_id, {
        decision: 'approve',
        overrideAccountId: accountId,
        createRule,
      });
      toast.success(createRule ? 'Got it — I’ll remember this for next time.' : 'Applied.');
      onItemHandled(current.suggestion_id);
      next();
    } catch (e) {
      toast.error(e.message || 'Apply failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ask-panel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-rose-600" />
            Help AI Buck learn
          </DialogTitle>
          <DialogDescription>
            {items.length === 0 ? "Nothing left to ask about." :
              `Item ${idx + 1} of ${items.length} — I'm not sure where these should go.`}
          </DialogDescription>
        </DialogHeader>

        {current ? (
          <div className="space-y-3" data-testid="ask-panel-card">
            <div className="rounded-xl border-2 border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/30 p-4 space-y-1">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Transaction I can&#39;t tell</div>
              <div className="text-lg font-semibold">{current.txn_name}</div>
              <div className="text-sm text-muted-foreground">
                {current.txn_date} · {fmtUSD(current.txn_amount)}
              </div>
              {current.rationale && (
                <div className="text-xs text-muted-foreground italic pt-2">&ldquo;{current.rationale}&rdquo;</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                Which account does this go to?
              </label>
              <select
                className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                defaultValue=""
                onChange={(e) => e.target.value && teach(e.target.value)}
                disabled={busy}
                data-testid="ask-account-select"
              >
                <option value="" disabled>Pick an account…</option>
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.name} ({a.type})
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <input
                  type="checkbox"
                  checked={createRule}
                  onChange={(e) => setCreateRule(e.target.checked)}
                  data-testid="ask-create-rule"
                />
                Remember this — apply automatically to future similar transactions
              </label>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" disabled={busy} onClick={() => { onItemHandled(current.suggestion_id); next(); }}
                data-testid="ask-skip-btn">
                Skip for now
              </Button>
              <Button
                variant="outline"
                disabled={busy}
                onClick={async () => {
                  try {
                    await applyOne(current.suggestion_id, { decision: 'reject' });
                    toast.success('Skipped — won’t ask again.');
                    onItemHandled(current.suggestion_id);
                    next();
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
                className="text-rose-600 hover:bg-rose-50"
                data-testid="ask-never-btn"
              >
                Don&#39;t categorize ever
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <div className="font-semibold">Thanks — that&#39;s everything.</div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ─── Preferences dialog ──────────────────────────────────────────────
// Per-user / per-workspace preferences for AI Buck. Confidence thresholds
// control which bucket each suggestion falls into on the action receipt.
// `confirm_everything` forces a manual review on every applied item.

const PreferencesDialog = ({ open, onClose, workspaceId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [highThr, setHighThr] = useState(0.9);
  const [medThr, setMedThr] = useState(0.6);
  const [confirmEverything, setConfirmEverything] = useState(false);
  const [autoCreateRules, setAutoCreateRules] = useState(true);

  useEffect(() => {
    if (!open || !workspaceId) return;
    setLoading(true);
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/agent/preferences?workspace_id=${workspaceId}`);
        if (r.ok) {
          const p = await r.json();
          setHighThr(p.high_confidence_threshold);
          setMedThr(p.medium_confidence_threshold);
          setConfirmEverything(!!p.confirm_everything);
          setAutoCreateRules(p.auto_create_rules !== false);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [open, workspaceId]);

  const save = async () => {
    if (medThr >= highThr) {
      toast.error('Medium threshold must be below the auto-apply threshold.');
      return;
    }
    setSaving(true);
    try {
      const r = await apiFetch(`${API_URL}/api/agent/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          high_confidence_threshold: highThr,
          medium_confidence_threshold: medThr,
          confirm_everything: confirmEverything,
          auto_create_rules: autoCreateRules,
        }),
      });
      if (!r.ok) throw new Error('Save failed');
      toast.success('Preferences saved.');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="agent-prefs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-violet-600" />
            AI Buck Preferences
          </DialogTitle>
          <DialogDescription>
            Tune how cautious AI Buck is. Higher thresholds = fewer auto-applies, more manual review.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Auto-apply threshold */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">Auto-apply threshold</label>
                <Badge variant="outline" data-testid="prefs-high-value">{Math.round(highThr * 100)}%</Badge>
              </div>
              <input
                type="range"
                min={0.5} max={1} step={0.05}
                value={highThr}
                onChange={(e) => setHighThr(parseFloat(e.target.value))}
                className="w-full accent-emerald-600"
                data-testid="prefs-high-slider"
              />
              <p className="text-xs text-muted-foreground">
                Suggestions ≥ this confidence are applied without asking.
              </p>
            </div>

            {/* Review threshold */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">Flag-for-review threshold</label>
                <Badge variant="outline" data-testid="prefs-medium-value">{Math.round(medThr * 100)}%</Badge>
              </div>
              <input
                type="range"
                min={0} max={0.95} step={0.05}
                value={medThr}
                onChange={(e) => setMedThr(parseFloat(e.target.value))}
                className="w-full accent-amber-600"
                data-testid="prefs-medium-slider"
              />
              <p className="text-xs text-muted-foreground">
                Suggestions between this and auto-apply go to the review queue. Below this — AI Buck asks you directly.
              </p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={confirmEverything}
                  onChange={(e) => setConfirmEverything(e.target.checked)}
                  data-testid="prefs-confirm-everything"
                />
                <span>
                  <span className="font-medium">Confirm everything</span>
                  <span className="block text-xs text-muted-foreground">
                    Force the action receipt to ask before any change — overrides the auto-apply threshold.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={autoCreateRules}
                  onChange={(e) => setAutoCreateRules(e.target.checked)}
                  data-testid="prefs-auto-rules"
                />
                <span>
                  <span className="font-medium">Auto-create rules from high-confidence suggestions</span>
                  <span className="block text-xs text-muted-foreground">
                    Next time a similar txn appears, it&apos;s categorized silently.
                  </span>
                </span>
              </label>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="prefs-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
